import React, { useState, useEffect } from 'react';
import apiClient from './api/client';

const Dashboard = ({ onLogout, user }) => {

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manualDeduction, setManualDeduction] = useState(() => {
    const stored = localStorage.getItem('manualDeduction');
    return stored ? parseFloat(stored) : 0;
  });
  // Date picker state
  const [selectedDate, setSelectedDate] = useState(() => {
    // Default to today in yyyy-mm-dd
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });


  useEffect(() => {
    // Always use persisted value on mount
    const stored = localStorage.getItem('manualDeduction');
    const deduction = stored ? parseFloat(stored) : 0;
    setManualDeduction(deduction);
    // Only fetch on mount, not on date change
    fetchDashboardData(deduction, selectedDate);
  }, []);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('Auto-refreshing dashboard data...');
      const stored = localStorage.getItem('manualDeduction');
      const deduction = stored ? parseFloat(stored) : 0;
      setManualDeduction(deduction);
      fetchDashboardData(deduction, selectedDate);
    }, 5 * 60 * 1000); // 5 minutes in milliseconds
    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [selectedDate]);

  const fetchDashboardData = async (deduction, date = selectedDate) => {
    // Always use the value from localStorage if not provided
    let finalDeduction = deduction;
    if (typeof finalDeduction === 'undefined') {
      const stored = localStorage.getItem('manualDeduction');
      finalDeduction = stored ? parseFloat(stored) : 0;
    }
    try {
      setLoading(true);
      const response = await apiClient.get('/api/dashboard', {
        params: { deduction: finalDeduction, date }
      });
      setDashboardData(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeductionChange = (value) => {
    const deduction = parseFloat(value) || 0;
    setManualDeduction(deduction);
    localStorage.setItem('manualDeduction', deduction);
    // Remove immediate API call - will be called on blur instead
  };

  const handleDeductionBlur = () => {
    fetchDashboardData(manualDeduction, selectedDate);
  };

  // Date picker change handler
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  // Date picker blur handler
  const handleDateBlur = () => {
    fetchDashboardData(manualDeduction, selectedDate);
  };

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--kidipoo-primary)' }}></div>
          <p className="text-xl text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <h2 className="text-xl font-bold text-red-800 mb-2">Error</h2>
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => {
                const stored = localStorage.getItem('manualDeduction');
                const deduction = stored ? parseFloat(stored) : 0;
                setManualDeduction(deduction);
                fetchDashboardData(deduction);
              }}
              className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header: Title always on top, controls below */}
        <div className="mb-2 sm:mb-4">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2" style={{ color: 'var(--kidipoo-primary)' }}>
            Kidipoo Ads Dashboard
          </h1>
          <div className="flex flex-row justify-between items-center">
            <p className="text-gray-600 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 w-fit">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd"/>
                </svg>
                Auto-refresh: 5min
              </span>
            </p>
            {/* Date Picker right */}
            <div className="flex flex-col items-end">
              <label htmlFor="dashboard-date" className="text-xs text-gray-600 mb-1">Select Date</label>
              <input
                type="date"
                id="dashboard-date"
                value={selectedDate}
                onChange={handleDateChange}
                onBlur={handleDateBlur}
                className="px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                max={new Date().toISOString().slice(0, 10)}
              />
            </div>
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* ROAS Ratio - First Card */}
          <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold" style={{ color: 'var(--kidipoo-primary)' }}>ROAS</h3>
              <div style={{ color: 'var(--kidipoo-primary)' }}>
                <svg className="w-6 sm:w-8 h-6 sm:h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd"/>
                </svg>
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">
              {dashboardData?.roas?.value?.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Based on adjusted net sales
              {dashboardData?.roas?.manual_deduction > 0 && (
                <span className="block text-xs text-orange-600">
                  (-₹{dashboardData.roas.manual_deduction})
                </span>
              )}
            </p>
          </div>

          {/* Shopify Net Sales */}
          <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold" style={{ color: 'var(--kidipoo-primary)' }}>Net Sales</h3>
              <div style={{ color: 'var(--kidipoo-primary)' }}>
                <svg className="w-6 sm:w-8 h-6 sm:h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"/>
                </svg>
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">
              {formatCurrency(dashboardData?.shopify?.adjusted_net_sales || 0, dashboardData?.shopify?.currency)}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Original: {formatCurrency(dashboardData?.shopify?.net_sales || 0, dashboardData?.shopify?.currency)}
            </p>

            {/* Manual Deduction Input */}
            <div className="mt-3 pt-3 border-t border-gray-100">
              <label htmlFor="deduction" className="block text-xs text-gray-600 mb-1">
                Manual Deduction
              </label>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">₹</span>
                <input
                  type="number"
                  id="deduction"
                  value={manualDeduction === 0 ? '' : manualDeduction}
                  onChange={(e) => handleDeductionChange(e.target.value)}
                  onBlur={handleDeductionBlur}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Total Ad Spend */}
          <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold" style={{ color: 'var(--kidipoo-primary)' }}>Ad Spend</h3>
              <div style={{ color: 'var(--kidipoo-primary)' }}>
                <svg className="w-6 sm:w-8 h-6 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">
              {formatCurrency(dashboardData?.total_ad_spend?.amount || 0, dashboardData?.total_ad_spend?.currency)}
            </p>
          </div>
        </div>

        {/* Detailed Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Meta Ads Breakdown */}
          <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200 shadow-sm">
            <h3 className="text-lg sm:text-xl font-semibold mb-4" style={{ color: 'var(--kidipoo-primary)' }}>Meta Ads Performance</h3>
            <div className="space-y-3 sm:space-y-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
                <span className="text-gray-700 text-sm sm:text-base">Monthly Account</span>
                <span className="text-gray-900 font-semibold text-sm sm:text-base">
                  {formatCurrency(dashboardData?.meta_ads?.monthly_account_spend || 0, dashboardData?.meta_ads?.currency)}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
                <span className="text-gray-700 text-sm sm:text-base">Prepaid Account</span>
                <span className="text-gray-900 font-semibold text-sm sm:text-base">
                  {formatCurrency(dashboardData?.meta_ads?.prepaid_account_spend || 0, dashboardData?.meta_ads?.currency)}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-3 sm:pt-4">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
                  <span className="text-gray-700 text-sm sm:text-base">Total Meta Spend</span>
                  <span className="text-gray-900 font-semibold text-sm sm:text-base">
                    {formatCurrency(dashboardData?.meta_ads?.total_spend || 0, dashboardData?.meta_ads?.currency)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Google Ads */}
          <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200 shadow-sm">
            <h3 className="text-lg sm:text-xl font-semibold mb-4" style={{ color: 'var(--kidipoo-primary)' }}>Google Ads Performance</h3>
            <div className="space-y-3 sm:space-y-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
                <span className="text-gray-700 text-sm sm:text-base">Total Spend</span>
                <span className="text-gray-900 font-semibold text-sm sm:text-base">
                  {formatCurrency(dashboardData?.google_ads?.total_spend || 0, dashboardData?.google_ads?.currency)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Refresh Button and Logout at bottom */}
        <div className="mt-6 sm:mt-8 flex flex-col items-center">
          <button
            onClick={() => {
              const stored = localStorage.getItem('manualDeduction');
              const deduction = stored ? parseFloat(stored) : 0;
              setManualDeduction(deduction);
              fetchDashboardData(deduction);
            }}
            className="px-4 sm:px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center space-x-2 mx-auto shadow-sm text-sm sm:text-base bg-[var(--kidipoo-primary)] hover:bg-[#0E8A8F] text-white"
          >
            <svg className="w-4 sm:w-5 h-4 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Refresh Now</span>
          </button>
          <p className="text-xs sm:text-sm text-gray-500 mt-2">Auto-refreshes every 5 minutes</p>
          <button
            onClick={onLogout}
            className="mt-4 px-4 sm:px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center space-x-2 shadow-sm text-sm sm:text-base bg-gray-100 hover:bg-gray-200 text-gray-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;